'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { ComponentProps, useRef } from 'react'
import styled, { ThemeProvider } from 'styled-components'

import { TypeStyleHeadlineExtraSmall, useLightTheme } from '@syconium/little-miss-figgy'
import {
  Dialog,
  DialogHeaderProps,
  DialogTitleProps,
} from '@syconium/little-miss-figgy/dist/components/Dialog'
import { DrawerDirection } from '@syconium/little-miss-figgy/dist/components/Dialog/Drawer'
import { DrawerProps } from '@syconium/little-miss-figgy/dist/components/Dialog/Drawer'
import { ModalProps } from '@syconium/little-miss-figgy/dist/components/Dialog/Modal'
import { IconButton } from '@syconium/little-miss-figgy/dist/components/IconButton/IconButton'
import { SelectMark } from '@syconium/little-miss-figgy/dist/components/Icons/Icon/SelectMark'
import { timingFaster, zIndexModal } from '@syconium/little-miss-figgy/dist/constants'

export const useNextDialogState = () => {
  return Dialog.useState()
}

export const useNextDialogContext = () => {
  return Dialog.useContext()
}

const StyledNextDialogBackdrop = styled(Dialog.Backdrop)<{ $timing?: string | number }>`
  backdrop-filter: blur(0px);
  background-color: rgba(0, 0, 0, 0.5);
  transition:
    opacity ${o => o.$timing ?? timingFaster} ease,
    background-color ${o => o.$timing ?? timingFaster} ease,
    backdrop-filter ${o => o.$timing ?? timingFaster} ease;
  &[data-enter] {
    backdrop-filter: blur(1px);
  }
`

const StyledDialogModal = styled(Dialog.Modal)<{ $timing: string | number }>`
  transition:
    transform ${o => o.$timing} ease,
    opacity ${o => o.$timing} ease;
  opacity: 0;
  transform: translateY(50px) scale(0.98);

  &[data-enter] {
    opacity: 1;
    transform: translateY(0px) scale(1);
  }
`

const StyledNexDialogHeader = styled(Dialog.Header)`
  align-items: flex-start;
  align-items: center;
`

const NoPaddingDialogContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0px;
  z-index: ${zIndexModal};
  overflow: auto;
  padding: 0;
`

export const NextDialogProvider = Dialog.Provider
export const NextDialogFooter = Dialog.Footer
export const NextDialogBackdrop = StyledNextDialogBackdrop

type NextDialogDismissProps = ComponentProps<typeof IconButton>

export const NextDialogDismiss = ({
  forwardedAs = Dialog.Dismiss,
  variant = 'black-on-white-no-border',
  flushTop = true,
  flushBottom = true,
  flushRight = true,
  flushLeft = true,
  ...props
}: NextDialogDismissProps) => (
  <IconButton
    forwardedAs={forwardedAs}
    variant={variant}
    flushRight={flushRight}
    flushTop={flushTop}
    flushBottom={flushBottom}
    flushLeft={flushLeft}
    {...props}
  >
    <SelectMark aria-hidden='true' />
  </IconButton>
)

export const NextDialogHeader = ({
  children,
  closeText,
  ...props
}: DialogHeaderProps & { children?: React.ReactNode } & {
  closeText: string
}) => (
  <StyledNexDialogHeader
    {...props}
    title={children ? <>{children}</> : null}
    dismiss={<NextDialogDismiss title={closeText} aria-label={closeText} />}
  />
)

export type NextDialogDrawerVariations = 'left-drawer' | 'right-drawer' | 'bottom-drawer'

type NextDialogBaseProps = {
  backdropTiming?: string | number
  closeOnSearchParamChange?: boolean
}

export type NextDialogModalProps = Omit<ModalProps, 'ref' | 'header' | 'footer'> &
  NextDialogBaseProps & {
    header?: ModalProps['header']
    footer?: ModalProps['footer']
    variant: 'default'
    dialogTiming?: string | number
  }

export type NextDialogDrawerProps = Omit<DrawerProps, 'dir'> &
  NextDialogBaseProps & {
    variant: NextDialogDrawerVariations
  }

export type NextDialogProps = NextDialogModalProps | NextDialogDrawerProps

const DRAWER_DIRECTIONS: Record<NextDialogDrawerVariations, DrawerDirection> = {
  'left-drawer': 'ltr',
  'right-drawer': 'rtl',
  'bottom-drawer': 'btt',
}

export const NextDialog = ({
  backdropTiming = timingFaster,
  backdrop = <StyledNextDialogBackdrop $timing={backdropTiming} />,
  unmountOnHide = true,
  header = null,
  closeOnSearchParamChange = false,
  children,
  ...props
}: NextDialogProps) => {
  const dialogContext = Dialog.useState()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const currentUrl =
    pathname + (closeOnSearchParamChange && searchParams ? `?${searchParams.toString()}` : '')
  const previousUrlRef = useRef(currentUrl)
  if (previousUrlRef.current !== currentUrl) {
    previousUrlRef.current = currentUrl
    dialogContext.hide()
  }

  // Use the current light theme for the dialog by default
  const lightTheme = useLightTheme()

  return (
    <ThemeProvider theme={lightTheme}>
      {props.variant === 'default' ? (
        <StyledDialogModal
          modal={true}
          {...props}
          unmountOnHide={unmountOnHide}
          backdrop={backdrop}
          header={header}
          footer={props.footer ?? null}
          $timing={props.dialogTiming ?? timingFaster}
        >
          {children}
        </StyledDialogModal>
      ) : (
        <Dialog.Drawer
          modal={true}
          {...props}
          unmountOnHide={unmountOnHide}
          backdrop={backdrop}
          header={header}
          // TODO: Fix this auto-generated suppression.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          dir={DRAWER_DIRECTIONS[props.variant] ?? 'ltr'}
        >
          {children}
        </Dialog.Drawer>
      )}
    </ThemeProvider>
  )
}

// This is a hack to center the title when the close button is on the right side
const StyledDialogTitle = styled(Dialog.Title)`
  ${o => o.align === 'center' && `margin-left: ${o.theme.spacing(6)};`}
`

export const NextDialogTitle = ({
  render = <TypeStyleHeadlineExtraSmall asTag='h2' isUppercase={true} textTransform='uppercase' />,
  ...props
}: DialogTitleProps) => <StyledDialogTitle render={render} {...props} />

export const NextDialogTrigger = (props: ComponentProps<typeof Dialog.Disclosure>) => (
  <Dialog.Disclosure {...props} />
)

export const renderWithNoPadding = (renderProps: React.HTMLAttributes<HTMLDivElement>) => (
  <NoPaddingDialogContainer>
    <div {...renderProps} />
  </NoPaddingDialogContainer>
)
